import styled from '@emotion/styled';
import React, { FC, useMemo, useState } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n, useSelector } from '~Hooks';
import { BlueButton, Box, Button, Input, RedButton } from '~Styles';
import { PREDEFINED_REASON_TYPE } from '../../../__queries__';
import ReasonButton from './ReasonButton';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import InfoIcon from '~Assets/icons/info.svg';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { useTableUniverse } from '~Components/TableUniverse';
import { getDataToUpdate } from '~Providers/CompetitionsProvider/utils';

const Wrapper = styled(Box)`
  margin: 0 0 25% 0;
  width: 368px;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
`;

const WasteButton = styled(BlueButton)`
  width: 136px;
`;

const CorrectionButton = styled(RedButton)`
  width: 136px;
`;

const CancelButton = styled(Button)`
  min-width: 40px;
  white-space: nowrap;
`;

const getInitialType = (
  isPOS: boolean,
  hasWasteReasons: boolean,
): PREDEFINED_REASON_TYPE | null => {
  /** If we are in POS and are registering waste */
  if (isPOS) {
    return PREDEFINED_REASON_TYPE.WASTE;
  }

  /** If there is waste reasons - allow the "Choose removal type -> Waste or Correction" */
  if (hasWasteReasons) {
    return null;
  }

  /** Standard if the customer doesn't use waste */
  return PREDEFINED_REASON_TYPE.CORRECTION;
};

interface Props {
  onComplete: (reason: string, commentOnWaste?: string) => void;

  /** If registering waste from QSR */
  isPOS?: boolean;

  /** Data below is needed to know if the items being removed are part of a competition so we can display a info box */
  chequeId?: string;
  itemIdsToRemove?: string[];
}

/**
 * Handles the removal of items from a cheque or when registering waste from QSR
 * Should it be logged as waste or correction?
 */
const RemoveItems: FC<Props> = ({ onComplete, isPOS, chequeId, itemIdsToRemove }) => {
  const { i18n } = useI18n();
  const competitions = useCompetition();
  const universe = useTableUniverse();
  const companyStaff = useSelector((state) => state.companyStaff?.staffs);

  const predefinedReasons = useSelector((state) => state.predefinedReasons);

  // Check if items being removed are part of any ongoing competition
  const competitionOwnerName = useMemo(() => {
    if (!chequeId || !itemIdsToRemove?.length || !competitions.onGoing?.length) {
      return null;
    }

    const cheque = universe.cheques.find((c) => c.id === chequeId);
    if (!cheque?.owner) return null;

    // Get the actual items being removed
    const itemsToRemove = universe.items.filter((item) => itemIdsToRemove.includes(item.id));
    if (!itemsToRemove.length) return null;

    // Check if any of the items are part of an ongoing competition
    const hasAffectedCompetition = competitions.onGoing.some((competition) => {
      const data = getDataToUpdate(competition, itemsToRemove);
      return data.amount > 0 || data.itemCount > 0;
    });

    if (!hasAffectedCompetition) return null;

    // Find the cheque owner's name
    return companyStaff?.find((s) => s.id === cheque.owner)?.name ?? null;
  }, [chequeId, itemIdsToRemove, competitions.onGoing, universe, companyStaff]);

  const hasWasteReasons = predefinedReasons.some(
    (reason) => reason.type === PREDEFINED_REASON_TYPE.WASTE,
  );

  /** This is only used for waste events since waste events use reason & comment */
  const [reason, setReason] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [typeToChoose, setTypeToChoose] = useState<PREDEFINED_REASON_TYPE | null>(
    getInitialType(Boolean(isPOS), Boolean(hasWasteReasons)),
  );

  const wasteButton = useButton({
    onClick: () => setTypeToChoose(PREDEFINED_REASON_TYPE.WASTE),
  });

  const correctionButton = useButton({
    onClick: () => setTypeToChoose(PREDEFINED_REASON_TYPE.CORRECTION),
  });

  const cancelButton = useButton({
    onClick: () => {
      setReason('');
      setComment('');
    },
    disabled: reason === '' && comment === '',
  });

  const confirmButton = useButton({
    onClick: () => {
      if (typeToChoose === PREDEFINED_REASON_TYPE.WASTE) {
        onComplete(reason, comment);
        return;
      }

      onComplete(reason !== '' ? `${reason} - ${comment}` : comment);
    },
    disabled: reason === '' && comment === '',
  });

  if (typeToChoose === null) {
    return (
      <Wrapper
        display="flex"
        flexDirection="column"
        gap="large"
        justifyContent="center"
        alignItems="center"
        padding="medium">
        {!isPOS && (
          <Heading level={1} color="var(--colorWhite)">
            {i18n('TM.RemovalType')}
          </Heading>
        )}
        <Box display="flex" gap="large">
          <WasteButton
            fluid
            size="large"
            display="flex"
            gap="xs"
            alignItems="center"
            {...wasteButton}>
            {i18n('TM.Waste')}
          </WasteButton>
          <CorrectionButton
            fluid
            size="large"
            display="flex"
            gap="xs"
            alignItems="center"
            {...correctionButton}>
            {i18n('TM.Correction')}
          </CorrectionButton>
        </Box>
      </Wrapper>
    );
  }

  const filteredReasons = predefinedReasons.filter((r) => r.type === typeToChoose);

  return (
    <Wrapper
      display="flex"
      flexDirection="column"
      gap="medium"
      justifyContent="center"
      alignItems="center"
      padding="medium">
      {filteredReasons.length > 0 && (
        <Box display="flex" flexDirection="column" gap="xs" fluid>
          <Heading level={4} color="var(--colorWhite)">
            {i18n('General.Reason')}
          </Heading>
          <Box display="flex" gap="small" flexWrap="wrap">
            {filteredReasons.map((r) => (
              <ReasonButton
                key={r.id}
                reasonEntered={reason}
                reason={r.text}
                onClick={() => {
                  setReason(r.text);
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="xs" fluid>
        {filteredReasons.length > 0 && (
          <Heading level={4} color="var(--colorWhite)">
            {i18n('General.CommentOptional')}
          </Heading>
        )}
        <Input
          autoFocus={true}
          value={comment}
          dark
          placeholder={
            typeToChoose === PREDEFINED_REASON_TYPE.WASTE
              ? filteredReasons.length === 0
                ? i18n('TM.ReasonForWaste')
                : i18n('RemoveItems.ExampleWasteComment')
              : filteredReasons.length === 0
              ? i18n('TM.ReasonForCorrection')
              : i18n('RemoveItems.ExampleCorrectionComment')
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setComment(e.target.value);
          }}
        />
      </Box>

      {competitionOwnerName && (
        <Box display="flex" flexDirection="row" gap="xs">
          <Icon src={InfoIcon} color="var(--colorBlueGray200)" height="32px" width="32px" />
          <Paragraph level={5} color="var(--colorBlueGray200)">
            {i18n('RemoveItems.CompetitionWarning', competitionOwnerName)}
          </Paragraph>
        </Box>
      )}

      <Box display="flex" flexDirection="row" gap="xs" alignItems="center" fluid>
        <CancelButton {...cancelButton}>{i18n('General.Clear')}</CancelButton>

        {typeToChoose === PREDEFINED_REASON_TYPE.WASTE ? (
          <BlueButton fluid {...confirmButton}>
            {i18n('Waste.Register')}
          </BlueButton>
        ) : (
          <RedButton fluid {...confirmButton}>
            {i18n('Order.Remove')}
          </RedButton>
        )}
      </Box>
    </Wrapper>
  );
};

export default RemoveItems;
